import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const PageHeader = styled.section`
  height: 300px;
  background: linear-gradient(rgba(255, 102, 0, 0.8), rgba(204, 82, 0, 0.8)), url("/images/srv_bg.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  h1 {
    color: white;
    font-size: 3rem;
    text-align: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 200px;
    h1 {
      font-size: 2rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #0a0a0a;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;

  .service-card {
    background: #1a1a1a;
    border-radius: 15px;
    overflow: hidden;
    border: 1px solid #333;
    transition: all 0.3s ease;

    &:hover {
      border-color: #ff6600;
      box-shadow: 0 10px 30px rgba(255, 102, 0, 0.2);
    }

    .service-image {
      height: 250px;
      background-size: cover;
      background-position: center;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(45deg, rgba(255, 102, 0, 0.8), rgba(204, 82, 0, 0.6));
      }
    }

    .service-content {
      padding: 2rem;
      position: relative;

      h3 {
        color: #ff6600;
        margin-bottom: 1rem;
        font-size: 1.3rem;
      }

      p {
        color: #b0b0b0;
        line-height: 1.6;
        margin-bottom: 1.5rem;
      }

      .service-features {
        list-style: none;
        padding: 0;
        margin-bottom: 1.5rem;

        li {
          padding: 0.5rem 0;
          color: #b0b0b0;
          position: relative;
          padding-left: 1.5rem;

          &:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #ff6600;
            font-weight: bold;
          }
        }
      }

      .service-link {
        display: inline-block;
        background: #ff6600;
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        text-decoration: none;
        font-weight: 500;
        transition: background 0.3s ease;

        &:hover {
          background: #cc5200;
        }
      }
    }
  }
`;

const CTASection = styled.section`
  padding: 80px 0;
  background: #111;
  text-align: center;

  h2 {
    color: #ff6600;
    margin-bottom: 1rem;
  }

  p {
    color: #b0b0b0;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    .btn {
      padding: 15px 30px;
      font-size: 1.1rem;
    }
  }
`;

const Services = () => {
  const services = [
    {
      id: "mobile-fitting",
      title: "Mobile Fleet Tyre Fitting",
      description: "Depot and roadside fitting for heavy vehicles — designed to minimise fleet downtime and keep trucks on the road.",
      image: "/images/ser_mtf.webp",
      features: ["Depot and roadside fitting", "Heavy-vehicle experienced technicians", "Rapid turnarounds for fleets"],
    },
    // Breakdown cover removed per request
    {
      id: "breakdown-cover",
      title: "Breakdown Cover",
      description: "24/7 breakdown cover to get you back on the road quickly with mobile assistance and tyre support.",
      image: "/images/24breakdown-cover.webp",
      features: ["Emergency roadside tyre assistance", "Priority scheduling", "Mobile technicians", "Nationwide coverage", "Transparent pricing"],
    },
    {
      id: "new-tyres",
      title: "Heavy-Duty New Tyres",
      description: "Premium truck tyres engineered for long-haul performance, high mileage and certified load ratings for commercial operations.",
      image: "/images/ser_rnt.webp",
      features: ["Long-life tread compounds", "Certified load ratings", "Fleet pricing available"],
    },
    {
      id: "puncture-repair",
      title: "Truck Tyre Repair",
      description: "Qualified heavy-vehicle tyre repairs and assessments, with depot or on-road service where safe and appropriate.",
      image: "/images/ser_tpr.webp",
      features: ["Safe repair protocols", "Depot and on-road assessments", "Repair reporting for fleet records"],
    },
    {
      id: "tyre-rotation",
      title: "Wheel Rotation & Alignment",
      description: "Fleet rotation, balancing and alignment services to extend tyre life and ensure stability under heavy loads.",
      image: "/images/ser_tirot.webp",
      features: ["Fleet rotation schedules", "Precision balancing", "Alignment for heavy loads"],
    },
  ];

  return (
    <>
      <PageHeader>
        <div className="container">
          <h1>Our Services</h1>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <div className="section-title">
            <h2>Comprehensive Tyre & Truck Solutions</h2>
            <p>
              In today's fast-paced world, not only is tyre technology rapidly evolving, but there is an ever-increasing number of high-quality
              alternatives to traditional brands available.
            </p>
          </div>

          <ServicesGrid>
            {services.map((service, index) => (
              <div
                key={service.id}
                className="service-card"
                id={service.id}>
                <div
                  className="service-image"
                  style={{ backgroundImage: `url(${service.image})` }}
                />
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>{feature}</li>
                    ))}
                  </ul>
                  <Link
                    to="/get-quote"
                    className="service-link">
                    Get Quote
                  </Link>
                </div>
              </div>
            ))}
          </ServicesGrid>
        </div>
      </ContentSection>

      <CTASection>
        <div className="container">
          <div>
            <h2>Need Professional Tyre Services?</h2>
            <p>
              Contact us today for expert advice, competitive quotes, and reliable service. Our team is ready to help you with all your tyre and truck
              solution needs.
            </p>
            <div className="cta-buttons">
              <Link
                to="/get-quote"
                className="btn">
                Get a Quote
              </Link>
              <Link
                to="/contact"
                className="btn btn-outline">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </CTASection>
    </>
  );
};

export default Services;
